<template>
  <div :class="['ai-data-card', `ai-data-card--${card.type}`]">
    <!-- 指标卡片 -->
    <template v-if="card.type === 'metric'">
      <div class="ai-metric-card">
        <div class="ai-metric-header">
          <span class="ai-metric-label">{{ card.label || card.title }}</span>
          <span v-if="card.trend" :class="['ai-metric-trend', `ai-metric-trend--${trendDir}`]">
            <Icon :name="trendIcon" :size="12" />
            <span>{{ card.trend }}</span>
          </span>
        </div>
        <div class="ai-metric-value">{{ card.value }}</div>
        <div v-if="card.subLabel" class="ai-metric-sub">{{ card.subLabel }}</div>
      </div>
    </template>

    <!-- 表格卡片 -->
    <template v-else-if="card.type === 'table'">
      <div class="ai-table-card">
        <div v-if="card.title" class="ai-table-card-title">{{ card.title }}</div>
        <div class="ai-table-card-body">
          <table class="ai-data-table">
            <thead>
              <tr>
                <th v-for="col in columns" :key="col">{{ col }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, ri) in rows" :key="ri">
                <td v-for="(cell, ci) in row" :key="ci">{{ cell }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <!-- 通用/未知类型回退 -->
    <template v-else>
      <div class="ai-generic-card">
        <div v-if="card.title" class="ai-generic-card-title">{{ card.title }}</div>
        <div v-if="card.content" class="ai-generic-card-content">{{ card.content }}</div>
        <pre v-else class="ai-generic-card-raw">{{ JSON.stringify(card, null, 2) }}</pre>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import Icon from '@/components/Icon/index.vue'

const props = defineProps({
  card: {
    type: Object,
    required: true
  }
})

// 表格列名
const columns = computed(() => {
  if (props.card.columns) return props.card.columns
  if (props.card.rows && props.card.rows.length > 0 && Array.isArray(props.card.rows[0])) {
    return props.card.rows[0]
  }
  return []
})

// 表格数据行
const rows = computed(() => {
  if (props.card.rows) {
    // 如果提供了 columns，则 rows 是纯数据；否则 rows[0] 是列名
    if (props.card.columns) return props.card.rows
    return props.card.rows.slice(1)
  }
  if (props.card.data && Array.isArray(props.card.data)) {
    return props.card.data
  }
  return []
})

// 趋势方向
const trendDir = computed(() => {
  const trend = String(props.card.trend || '')
  if (trend.startsWith('-') || trend.startsWith('↓')) return 'down'
  if (trend.startsWith('+') || trend.startsWith('↑')) return 'up'
  return 'flat'
})

const trendIcon = computed(() => {
  if (trendDir.value === 'up') return 'TrendingUp'
  if (trendDir.value === 'down') return 'TrendingDown'
  return 'Minus'
})
</script>

<script>
export default { name: 'AiDataCard' }
</script>

<style lang="scss">
.ai-data-card {
  margin: 8px 0;
  border-radius: 8px;
  border: 1px solid var(--el-border-color-light);
  overflow: hidden;
  background: var(--el-bg-color);
}

/* 指标卡片 */
.ai-metric-card {
  padding: 12px;

  .ai-metric-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
  }

  .ai-metric-label {
    font-size: 12px;
    color: var(--el-text-color-secondary);
  }

  .ai-metric-value {
    font-size: 22px;
    font-weight: 700;
    color: var(--el-text-color-primary);
    line-height: 1.3;
  }

  .ai-metric-sub {
    font-size: 12px;
    color: var(--el-text-color-placeholder);
    margin-top: 2px;
  }

  .ai-metric-trend {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    font-size: 12px;
    font-weight: 500;
    padding: 1px 6px;
    border-radius: 4px;

    &--up {
      color: var(--el-color-success);
      background: var(--el-color-success-light-9);
    }

    &--down {
      color: var(--el-color-danger);
      background: var(--el-color-danger-light-9);
    }

    &--flat {
      color: var(--el-text-color-secondary);
      background: var(--el-fill-color-light);
    }
  }
}

/* 表格卡片 */
.ai-table-card {
  .ai-table-card-title {
    padding: 8px 12px;
    font-size: 13px;
    font-weight: 600;
    color: var(--el-text-color-primary);
    border-bottom: 1px solid var(--el-border-color-lighter);
    background: var(--el-fill-color-lighter);
  }

  .ai-table-card-body {
    overflow-x: auto;
  }
}

.ai-data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;

  th, td {
    padding: 5px 10px;
    text-align: left;
    white-space: nowrap;
    border-bottom: 1px solid var(--el-border-color-extra-light);
  }

  th {
    background: var(--el-fill-color-lighter);
    font-weight: 600;
    color: var(--el-text-color-regular);
  }

  tr:last-child td {
    border-bottom: none;
  }

  tr:hover td {
    background: var(--el-fill-color-lighter);
  }
}

/* 通用卡片 */
.ai-generic-card {
  padding: 12px;

  .ai-generic-card-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--el-text-color-primary);
    margin-bottom: 4px;
  }

  .ai-generic-card-content {
    font-size: 13px;
    color: var(--el-text-color-regular);
    line-height: 1.5;
  }

  .ai-generic-card-raw {
    margin: 0;
    padding: 8px;
    background: var(--el-fill-color-lighter);
    border-radius: 4px;
    font-size: 12px;
    font-family: 'Consolas', 'Monaco', monospace;
    white-space: pre-wrap;
    word-break: break-all;
    color: var(--el-text-color-secondary);
  }
}
</style>
